import { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Loader, RequestedDataRenderer } from '@components';
import { CONSTANT } from '@const';
import { AccountContext } from '@context';
import { TermsAndConditionsService } from '@data';
import { Button, CheckboxField, Modal, Status } from '@general';
import { msalService } from '@services';
import { safeId, useLogout } from '@utils';

import styles from './termsAndConditions.module.scss';

const MIN_LONG_CONTENT_LENGTH = 12000;

export const TermsAndConditions = () => {
	// Context
	const { getSelectedAccount } = useContext(AccountContext);
	// State
	const { inProgress } = useMsal();
	const [openTC, setOpenTC] = useState<boolean>(false);
	const [isContentLong, setIsContentLong] = useState(false);
	const [isRead, setIsRead] = useState(false);
	const [isAgreed, setIsAgreed] = useState(false);
	const [declineMode, setDeclineMode] = useState(false);
	// Others
	const logout = useLogout();

	// Data
	const getTermsConditions = useQuery({
		queryKey: [CONSTANT.TERMS_AND_CONDITIONS_API_URL],
		queryFn: () => TermsAndConditionsService.getTermsAndConds(),
		enabled:
			msalService.isValidAccount() &&
			!getSelectedAccount?.isLoading &&
			(!getSelectedAccount?.data || !getSelectedAccount?.data.isCurrentTandC),
	});

	useEffect(() => {
		if (getTermsConditions.data) {
			setIsContentLong(getTermsConditions.data.content?.length >= MIN_LONG_CONTENT_LENGTH);
		}
	}, [getTermsConditions.data]);
	const acceptTermsAndConds = useMutation({
		mutationFn: (termsAndConditionsId: string) =>
			TermsAndConditionsService.acceptTermsAndConds(termsAndConditionsId),
	});

	// Life cycle
	useEffect(() => {
		if (inProgress === InteractionStatus.None) {
			const isDataLoadOnError =
				getTermsConditions.data && !getSelectedAccount?.isLoading && !getSelectedAccount?.data;
			const isDataLoad = getTermsConditions.data && getSelectedAccount?.data;
			const hideOnInitialization = !getTermsConditions.isError && !isDataLoad;
			if (isDataLoadOnError) {
				setOpenTC(true);
			} else if (hideOnInitialization) {
				setOpenTC(false);
			} else {
				setOpenTC(!getSelectedAccount?.data?.isCurrentTandC);
			}
		}
	}, [
		inProgress,
		getTermsConditions.data,
		getSelectedAccount?.data,
		getTermsConditions.isError,
		getSelectedAccount?.isLoading,
	]);

	// Helper
	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
		const isBottom = scrollHeight - scrollTop <= clientHeight + 40;
		if (!isRead) {
			setIsRead(isBottom);
		}
	};

	const readAgain = () => {
		setDeclineMode(false);
		setIsRead(false);
		setIsAgreed(false);
		if (!getTermsConditions.data) {
			getTermsConditions.refetch();
		}
	};

	const retry = () => {
		if (getTermsConditions.isError) {
			getTermsConditions.refetch();
		} else if (acceptTermsAndConds.isError) {
			accept();
		}
	};

	const accept = async () => {
		await acceptTermsAndConds.mutateAsync(safeId(getTermsConditions.data?.id)).then(() => {
			setOpenTC(false);
		});
	};

	const TermsAndCondsContent = getTermsConditions.data ? (
		<>
			<div
				className={styles.content}
				onScroll={handleScroll}
				dangerouslySetInnerHTML={{
					__html: getTermsConditions.data.content,
				}}
			></div>
			<div className={styles['status-container']}>
				<Status
					status="error"
					show={acceptTermsAndConds.isError}
					onClose={acceptTermsAndConds.reset}
					msg="The server encountered an error while accepting the Terms and Conditions. Please try again later."
				/>
			</div>
		</>
	) : (
		<></>
	);

	return (
		<>
			<Modal open={openTC} customModalStyle={styles[`modal${declineMode ? '-decline' : ''}`]}>
				{declineMode ? (
					<div className={styles.decline}>
						<p>To access the WaterIQ website, please read and accept the terms and conditions.</p>
						<div className={styles['decline-btn-group']}>
							<Button onClick={logout} size="small" aria-label="Logout" type="secondary">
								Log out
							</Button>
							<Button onClick={readAgain} type="primary" size="large">
								Read again
							</Button>
						</div>
					</div>
				) : (
					<>
						<RequestedDataRenderer
							fulfilledComponent={TermsAndCondsContent}
							emptyComponent={<></>}
							loaderComponent={
								<div className={styles.loader}>
									<Loader />
								</div>
							}
							errorComponent={
								<div className={styles.error}>
									<p>The server encountered an error while retrieving the Terms and Conditions.</p>
									<br></br>
									<p>Please try again later.</p>
								</div>
							}
							queryStatus={getTermsConditions.status}
							data={getTermsConditions.data}
							error={getTermsConditions.error}
						/>
						<div
							style={{
								visibility: getTermsConditions.isSuccess ? 'visible' : 'hidden',
							}}
						>
							<CheckboxField
								value={isAgreed}
								onChange={setIsAgreed}
								disabled={isContentLong && !isRead && !isAgreed}
							>
								I agree with the <b>Terms and Conditions</b>
							</CheckboxField>
						</div>
						{getTermsConditions.isError || acceptTermsAndConds.isError ? (
							<div className={styles.buttons}>
								<Button onClick={logout} size="small" type="secondary">
									Cancel
								</Button>
								<Button onClick={retry} size="small" type="primary">
									Retry
								</Button>
							</div>
						) : (
							<>
								<div className={styles.buttons}>
									<Button onClick={() => setDeclineMode(true)} size="small" type="secondary">
										{getTermsConditions.isLoading ? 'Cancel' : 'Decline'}
									</Button>
									<Button
										onClick={accept}
										size="small"
										type={isAgreed ? 'primary' : 'disabled'}
										isLoading={acceptTermsAndConds.isPending}
									>
										Accept
									</Button>
								</div>
							</>
						)}
					</>
				)}
			</Modal>
		</>
	);
};
